import * as template from './result.hbs';
import './tidings.less';
import 'font-awesome/css/font-awesome.css';
import Component from '../component/component';
import { PublicationTypes, Batch, Publication, Field, Header, Image } from './model';
import { fadeIn } from '../animations/fading';
import { busy, ready } from '../animations/busy';
import TidingsClient from './tidingsClient';

class ViewModel {
    batch?: Batch;
    publicationCount: number;
    unapprovedPublicationCount: number;
    totalPublicationCount: number;
    headers: Header[];
    firstUnapprovedAnchorId: string | undefined;
}


export default class Result extends Component<ViewModel> {

    private _tidingsClient: TidingsClient;
    private _approveButtons: HTMLInputElement[];
    private _rejectButtons: HTMLInputElement[];

    constructor() {
        super(template, new ViewModel());

        this._tidingsClient = new TidingsClient();
    }

    protected bind(element: Element, _model: ViewModel) {
        this._approveButtons = Array.from(element.querySelectorAll<HTMLInputElement>('button[id^="approveButton_"]'));
        this._rejectButtons = Array.from(element.querySelectorAll<HTMLInputElement>('button[id^="rejectButton_"]'));

        this._approveButtons.forEach(button =>
            button.addEventListener('click', async _ => (await this.approve(parseInt(button.id.replace(/[a-z_]/gi, '')), button))));

        this._rejectButtons.forEach(button =>
            button.addEventListener('click', async _ => (await this.reject(parseInt(button.id.replace(/[a-z_]/gi, '')), button))));

        window.onresize = this.resize;
    }

    private async approve(id: number, button: HTMLElement) {
        let busyIndicator = button.parentElement!.querySelector<HTMLElement>('i.busy-indicator');
        busy(busyIndicator);
        this._approveButtons.forEach(b => b.disabled = true);
        this._rejectButtons.forEach(b => b.disabled = true);

        let publication = this.getPublication(id);

        if (publication !== undefined) {
            let approvedId = (await this._tidingsClient.approve(<Publication>publication)).Id;

            this.viewModel.headers
                .forEach(h => h.Publications
                    .filter(p => p.Id === id)
                    .forEach(p => p.Id = approvedId));

            this.refresh();
        }
    }

    private async reject(id: number, button: HTMLElement) {
        let cancel = 'Cancel';

        if (navigator.language.indexOf('da') === 0) {
            cancel = 'Annuller';
        }

        if (window.confirm('Vil du afvise denne publicering?\n\n' +
            'Hvis du afviser publiceringen vil der skulle laves en ny for at f' + unescape('%E5') + ' publiceret h' + unescape('%E6') + 'ndelsen.\n\n' +
            'Tryk p' + unescape('%E5') + ' \'OK\' for at afvise publiceringen.\n' +
            'Tryk p' + unescape('%E5') + ' \'' + cancel + '\' for at beholde publiceringen.')) {

            let busyIndicator = button.parentElement!.querySelector<HTMLElement>('i.busy-indicator')
            busy(busyIndicator);
            this._approveButtons.forEach(b => b.disabled = true);
            this._rejectButtons.forEach(b => b.disabled = true);

            let publication = this.getPublication(id);

            if (publication !== undefined) {
                publication = await this._tidingsClient.reject(<Publication>publication);

                this.removePublication(id);
                this.refresh();
            }
        }
    }

    private getPublication(id: number): Publication | undefined {
        for (let header of this.viewModel.headers) {
            for (let publication of header.Publications) {
                if (publication.Id === id) {
                    return publication;
                }
            }
        }
        return undefined;
    }

    private removePublication(id: number) {
        for (let header of this.viewModel.headers) {
            let publicationIndex = this.findIndexById(id, header.Publications);
            if (publicationIndex !== -1) {
                header.Publications.splice(publicationIndex, 1);

                if (header.Publications.length === 0) {
                    let headerIndex = this.findIndexByUniqueId(header.UniqueId, this.viewModel.headers);
                    if (headerIndex !== -1) {
                        this.viewModel.headers.splice(headerIndex, 1);
                    }
                }
            }
        }
    }

    private sortFormatter(value: string | null): string {

        value = value || '';

        if (value.match(/\s*[A-Z]\s*\d{2}\s*[A-Z]\s+\d+\/\d+/)) {
            return value.replace(/ (\d+)\//,
                (s: string, c: string) => {
                    while (c.length < 3) c = `0${c}`;
                    return ` ${c}/`;
                });
        }

        return value;
    }

    public showBatch(batch: Batch) {
        delete this.viewModel.headers;
        this.viewModel.batch = batch;
        this.formatBatch();
        fadeIn(this.element);
        this.refresh();
    }

    protected refresh() {

        let alldistinctPublications: Publication[] = [];
        let allPublications: Publication[] = [];

        if (this.viewModel.headers !== undefined) {
            this.viewModel.headers.forEach(h => {
                let distinctPublications = this.distinctById(h.Publications);
                h.publicationCount = distinctPublications.filter(p => p.Id > 0).length;
                h.unapprovedPublicationCount = distinctPublications.filter(p => p.Id < 0).length;
                alldistinctPublications = alldistinctPublications.concat(distinctPublications);
                allPublications = allPublications.concat(h.Publications);
            });
        }

        alldistinctPublications = this.distinctById(alldistinctPublications);
        this.viewModel.totalPublicationCount = alldistinctPublications.length;
        this.viewModel.publicationCount = alldistinctPublications.filter(p => p.Id > 0).length;
        this.viewModel.unapprovedPublicationCount = alldistinctPublications.filter(p => p.Id < 0).length;

        let publicationCounts: { [id: number]: number; } = {}

        allPublications.forEach(p => {
            p.isUnapproved = p.Id < 0;
            let count = publicationCounts[p.Id] || 0;
            p.anchorId = '' + p.Id + (count ? '_' + count : '')
            publicationCounts[p.Id] = count + 1;
        });

        allPublications.forEach((p, i) => {
            p.prevUnapprovedAnchorId = this.findUnapprovedAnchorId(allPublications.slice(0, i).reverse());
            p.nextUnapprovedAnchorId = this.findUnapprovedAnchorId(allPublications.slice(i + 1));
        });
        if (this.viewModel.headers !== undefined) {
            this.viewModel.headers.forEach(h => {
                h.firstUnapprovedAnchorId = this.findUnapprovedAnchorId(h.Publications);
            });
        }
        this.viewModel.firstUnapprovedAnchorId = this.findUnapprovedAnchorId(allPublications);

        super.refresh();

        this.resize();
    }

    private findUnapprovedAnchorId(publications: Publication[]): string | undefined {
        for (let publication of publications) {
            if (publication.Id < 0) {
                return publication.anchorId;
            }
        }

        return undefined;
    }


    private distinctById(publications: Publication[]): Publication[] {
        let distinct: Publication[] = [];

        for (let publication of publications) {
            if (this.findIndexById(publication.Id, distinct) === -1) {
                distinct.push(publication);
            }
        }

        return distinct;
    }

    private findIndexById(id: number, publications: Publication[]): number {
        for (let i = 0; i < publications.length; i++) {
            if (publications[i].Id === id) return i;
        }
        return -1;
    }

    private findIndexByUniqueId(uniqueId: number, headers: Header[]): number {
        for (let i = 0; i < headers.length; i++) {
            if (headers[i].UniqueId === uniqueId) return i;
        }
        return -1;
    }

    private resize() {
        let height = (<HTMLElement>document.querySelector('.fixedtop')).offsetHeight,
            topdist = (-1 * height).toString() + 'px';
        (<HTMLElement>document.querySelector('.scrollablecontent')).style.marginTop = height + 10 + 'px';

        Array.from(document.querySelectorAll('a[id^="Header"]')).forEach(e => {
            (<HTMLElement>e).style.top = topdist;
        });

        Array.from(document.querySelectorAll('a[id^="Publication"]')).forEach(e => {
            (<HTMLElement>e).style.top = topdist;
        });

        let top = <HTMLElement>document.querySelector('#top');
        if (top) {
            top.style.top = topdist;
        }
    }


    public showPublications(headers: Header[]) {

        this.mapHoverTexts(headers, 'Patent', 11, '(A8) Patentans&oslash;gning med rettet forside\r\n(A9) Rettet patentans&oslash;gning');
        this.mapHoverTexts(headers, 'Patent', 12, '(B8) Patent med rettet forside\r\n(B9) Rettet patent');

        this.viewModel.headers = headers;
        this.viewModel.batch = undefined;

        let previousTitle = '';
        for (let header of headers) {
            header.belongsToPrevious = previousTitle === header.Title;
            for (let publication of header.Publications) {
                this.viewModel.batch = this.viewModel.batch || publication.Batch;
                publication.PublicationTime = publication.PublicationTime;
                publication.Fields = publication.Fields.filter(f => f.Headers.some(fh => fh.UniqueId === header.UniqueId) && (f.Text || f.Images.length > 0)).map(f => {
                    f.Text = f.Text && f.Text.replace(/\n/g, '<br />');
                    f.Images = f.Images.map(i => {
                        i.imageSource = './odata/Image/Tidings.ImageData(imageId=' + i.Id + ')' + (publication.Id < 0 ? '?Unapproved' : '');
                        return i;
                    });
                    return f;
                });
            }
            previousTitle = header.Title;
        }

        this.orderPublicationsByFieldId(headers);

        this.formatBatch();
        fadeIn(this.element);
        this.refresh();
    }

    private formatBatch() {
        if (this.viewModel.batch) {
            this.viewModel.batch.tidendeTypeNavn =
                (this.viewModel.batch.PublicationType ? PublicationTypes[this.viewModel.batch.PublicationType] : this.viewModel.batch.PublicationType).replace(/^[a-z]/, '$1'.toUpperCase());

            this.viewModel.batch.FirstDate = this.viewModel.batch.FirstDate.substr(0, 10);
            this.viewModel.batch.LastDate = this.viewModel.batch.LastDate.substr(0, 10);

            this.viewModel.batch.closed = new Date().toISOString().substr(0, 10) > this.viewModel.batch.LastDate;
        }
    }

    private orderPublicationsByFieldId(headers: Header[]) {
        for (let header of headers) {
            if (header.OrderByFieldId) {
                let contentIds = header.OrderByFieldId.split(',').map(i => parseInt(i));
                let publications: Publication[] = [];
                header.Publications.forEach(p => publications = publications.concat(this.markAndMultiply(header, p, contentIds)));

                header.Publications = publications.sort((p1, p2) => this.sortFormatter(p1.sortText).localeCompare(this.sortFormatter(p2.sortText)));
            }
        }
    }

    private markAndMultiply(header: Header, publication: Publication, contentIds: number[]) {
        let contentIdAndSortTexts: ContentIdAndSortText[] = [];
        let separator = (header.MultiValueSeparator != null && header.MultiValueSeparator.length)
            ? header.MultiValueSeparator.replace('\\n', '<br />')
            : null;

        for (let field of publication.Fields) {
            if (contentIds.filter(ci => ci === field.ContentId).length) {
                let texts = separator
                    ? field.Text.split(separator).filter(t => t).map(t => t.trim()).filter(t => t.length)
                    : [field.Text.trim()];

                texts.forEach(sortText => {
                    contentIdAndSortTexts.push(new ContentIdAndSortText(field.ContentId, sortText));
                });
            }
        }

        return contentIdAndSortTexts.map(cs => this.clonePublication(publication, cs));
    }

    private clonePublication(publication: Publication, contentIdAndSortText: ContentIdAndSortText): Publication {
        let newPublication: Publication =
        {
            Id: publication.Id,
            PublicationType: publication.PublicationType,
            PublicationTime: publication.PublicationTime,
            Batch: publication.Batch,
            Headers: publication.Headers,
            Fields: publication.Fields.map(f => {
                let newField: Field = {
                    Id: f.Id,
                    Inid: f.Inid,
                    ContentId: f.ContentId,
                    sortText: contentIdAndSortText.contentId === f.ContentId ? contentIdAndSortText.sortText : null,
                    Headers: f.Headers,
                    Images: f.Images.map(i => {
                        let newImage: Image = {
                            Id: i.Id,
                            ImageFormat: i.ImageFormat,
                            imageSource: i.imageSource,
                            Reference: i.Reference,
                        }
                        return newImage;
                    }),
                    Label: f.Label,
                    Order: f.Order,
                    Text: f.Text,
                };
                return newField;
            }),
            sortText: contentIdAndSortText.sortText,
            isUnapproved: false,
            anchorId: undefined,
            nextUnapprovedAnchorId: undefined,
            prevUnapprovedAnchorId: undefined,
        };
        return newPublication;
    }

    private mapHoverTexts(headers: Header[], publicationType: string, uniqueId: number, hoverText: string) {
        for (let he of headers) {
            if (he.PublicationType === publicationType && he.UniqueId === uniqueId) {
                he.hoverText = hoverText;
            }
        }
    }


}

class ContentIdAndSortText {
    constructor(public contentId: number, public sortText: string) { }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./Frontend/src/Tidings/result.ts